import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/AdminLayout";
import { api, errText } from "@/lib/api";
import { copyText } from "@/lib/partners";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ChevronDown, Copy, Download, ExternalLink, Pencil, CheckCircle2, Zap } from "lucide-react";

const F = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] uppercase tracking-[0.14em] text-gray-500">{label}</Label>
    {children}
  </div>
);

const EMPTY = {
  first_name: "", last_name: "", brokerage: "", city: "", email: "", phone: "", website: "", instagram: "",
  linkedin: "", team_name: "", territory: "", headshot_url: "", brokerage_logo_url: "", realtor_logo_url: "",
  estimated_transactions: "", estimated_volume: "", average_home_price: "", years_active: "", prospect_score: "",
  notes: "",
};

export default function RealtorNew() {
  const [form, setForm] = useState(EMPTY);
  const [more, setMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const navigate = useNavigate();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.first_name.trim() || !form.brokerage.trim() || !form.city.trim() || !form.email.trim()) {
      toast.error("Realtor name, brokerage, city and email are required");
      return;
    }
    setSaving(true);
    try {
      const num = (v) => Number(String(v).replace(/[^0-9.]/g, "")) || 0;
      const { data } = await api.post("/realtors", {
        ...form,
        estimated_transactions: num(form.estimated_transactions),
        estimated_volume: num(form.estimated_volume),
        average_home_price: num(form.average_home_price),
        years_active: num(form.years_active),
        prospect_score: num(form.prospect_score),
      });
      setCreated(data);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSaving(false);
    }
  };

  const markReady = async () => {
    await api.post("/realtors/bulk", { ids: [created.id], action: "mark_ready" });
    toast.success("Marked Ready to Contact");
  };

  if (created) {
    const l = created.links;
    return (
      <AdminLayout>
        <div className="max-w-3xl mx-auto text-center py-6 sm:py-12 fade-up" data-testid="realtor-created-screen">
          <CheckCircle2 size={52} className="text-[#c19a6b] mx-auto mb-8" strokeWidth={1.2} />
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight">
            {created.display_name}'s ARK Private Client Program is ready.
          </h1>
          <p className="text-gray-500 mt-4 text-sm">
            Presentation, client page, QR code and tracking link have all been generated.
          </p>

          <div className="grid sm:grid-cols-2 gap-3 mt-12 text-left">
            <Button data-testid="view-presentation-btn" asChild className="h-12 bg-black hover:bg-black/85 text-white rounded-sm text-xs uppercase tracking-[0.15em] btn-lift">
              <a href={l.presentation_url} target="_blank" rel="noreferrer"><ExternalLink size={14} className="mr-2" /> View Realtor Presentation</a>
            </Button>
            <Button data-testid="view-client-page-btn" asChild className="h-12 bg-[#c19a6b] hover:bg-[#b08a5c] text-white rounded-sm text-xs uppercase tracking-[0.15em] btn-lift">
              <a href={l.client_url} target="_blank" rel="noreferrer"><ExternalLink size={14} className="mr-2" /> View Client Page</a>
            </Button>
            <Button data-testid="copy-presentation-link-btn" variant="outline" onClick={() => copyText(l.presentation_url, "Presentation link copied")} className="h-12 rounded-sm text-xs uppercase tracking-[0.15em]">
              <Copy size={14} className="mr-2" /> Copy Presentation Link
            </Button>
            <Button data-testid="copy-client-link-btn" variant="outline" onClick={() => copyText(l.client_url, "Client link copied")} className="h-12 rounded-sm text-xs uppercase tracking-[0.15em]">
              <Copy size={14} className="mr-2" /> Copy Client Link
            </Button>
            <Button data-testid="download-qr-btn" asChild variant="outline" className="h-12 rounded-sm text-xs uppercase tracking-[0.15em]">
              <a href={`${l.qr_image_url}?size=14`} download={`ark-${created.slug}-qr.png`}><Download size={14} className="mr-2" /> Download QR Code</a>
            </Button>
            <Button data-testid="edit-realtor-btn" variant="outline" onClick={() => navigate(`/admin/realtors/${created.id}`)} className="h-12 rounded-sm text-xs uppercase tracking-[0.15em]">
              <Pencil size={14} className="mr-2" /> Edit Realtor
            </Button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4 mt-10">
            <Button data-testid="mark-ready-btn" onClick={markReady} variant="ghost" className="text-xs uppercase tracking-[0.15em] text-[#8a6a42]">
              <Zap size={14} className="mr-2" /> Mark Ready to Contact
            </Button>
            <button
              data-testid="create-another-btn"
              onClick={() => { setCreated(null); setForm(EMPTY); }}
              className="text-sm text-gray-500 hover:text-black underline underline-offset-4"
            >
              Create another realtor
            </button>
            <Link to="/admin/realtors" className="text-sm text-gray-500 hover:text-black underline underline-offset-4">
              Back to partners
            </Link>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="New Realtor Partner">
      <div className="max-w-2xl space-y-6">
        <div className="bg-white border border-gray-200 p-6 sm:p-8">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#c19a6b] font-bold mb-6">Built in under 60 seconds</p>
          <div className="grid sm:grid-cols-2 gap-5">
            <F label="First Name *"><Input data-testid="realtor-first-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="rounded-sm h-11" /></F>
            <F label="Last Name"><Input data-testid="realtor-last-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="rounded-sm h-11" /></F>
            <F label="Brokerage *"><Input data-testid="realtor-brokerage" placeholder="RE/MAX" value={form.brokerage} onChange={(e) => set("brokerage", e.target.value)} className="rounded-sm h-11" /></F>
            <F label="City *"><Input data-testid="realtor-city" placeholder="Whitby, Ontario" value={form.city} onChange={(e) => set("city", e.target.value)} className="rounded-sm h-11" /></F>
            <F label="Email *"><Input data-testid="realtor-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="rounded-sm h-11" /></F>
            <F label="Phone"><Input data-testid="realtor-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-sm h-11" /></F>
          </div>

          <button
            data-testid="toggle-optional-fields"
            onClick={() => setMore((m) => !m)}
            className="flex items-center gap-2 mt-7 text-xs uppercase tracking-[0.15em] text-gray-500 hover:text-black transition-colors duration-200"
          >
            <ChevronDown size={14} className={`transition-transform duration-200 ${more ? "rotate-180" : ""}`} />
            {more ? "Hide" : "Add"} optional research details
          </button>

          {more && (
            <div className="grid sm:grid-cols-2 gap-5 mt-6 pt-6 border-t border-gray-100">
              <F label="Team / Realtor Name"><Input data-testid="realtor-team" value={form.team_name} onChange={(e) => set("team_name", e.target.value)} className="rounded-sm h-11" /></F>
              <F label="Primary Territory"><Input data-testid="realtor-territory" value={form.territory} onChange={(e) => set("territory", e.target.value)} className="rounded-sm h-11" /></F>
              <F label="Website"><Input data-testid="realtor-website" value={form.website} onChange={(e) => set("website", e.target.value)} className="rounded-sm h-11" /></F>
              <F label="Instagram"><Input data-testid="realtor-instagram" placeholder="@handle" value={form.instagram} onChange={(e) => set("instagram", e.target.value)} className="rounded-sm h-11" /></F>
              <F label="LinkedIn"><Input data-testid="realtor-linkedin" value={form.linkedin} onChange={(e) => set("linkedin", e.target.value)} className="rounded-sm h-11" /></F>
              <F label="Headshot Image URL"><Input data-testid="realtor-headshot" value={form.headshot_url} onChange={(e) => set("headshot_url", e.target.value)} className="rounded-sm h-11" /></F>
              <F label="Brokerage Logo URL"><Input data-testid="realtor-brokerage-logo" value={form.brokerage_logo_url} onChange={(e) => set("brokerage_logo_url", e.target.value)} className="rounded-sm h-11" /></F>
              <F label="Annual Transactions"><Input data-testid="realtor-transactions" value={form.estimated_transactions} onChange={(e) => set("estimated_transactions", e.target.value)} className="rounded-sm h-11" /></F>
              <F label="Annual Sales Volume ($)"><Input data-testid="realtor-volume" value={form.estimated_volume} onChange={(e) => set("estimated_volume", e.target.value)} className="rounded-sm h-11" /></F>
              <F label="Average Home Price ($)"><Input data-testid="realtor-avg-price" value={form.average_home_price} onChange={(e) => set("average_home_price", e.target.value)} className="rounded-sm h-11" /></F>
              <F label="Years in Business"><Input data-testid="realtor-years" value={form.years_active} onChange={(e) => set("years_active", e.target.value)} className="rounded-sm h-11" /></F>
              <F label="ARK Prospect Score (0-100)"><Input data-testid="realtor-score" value={form.prospect_score} onChange={(e) => set("prospect_score", e.target.value)} className="rounded-sm h-11" /></F>
              <div className="sm:col-span-2">
                <F label="Notes"><Textarea data-testid="realtor-notes" rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="rounded-sm" /></F>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <Button
            data-testid="save-realtor-btn"
            onClick={submit}
            disabled={saving}
            className="h-12 px-10 bg-[#c19a6b] hover:bg-[#b08a5c] text-white rounded-sm tracking-[0.15em] uppercase text-xs btn-lift"
          >
            {saving ? "Building..." : "Build Their Program"}
          </Button>
          <Button asChild variant="outline" className="h-12 px-6 rounded-sm tracking-[0.15em] uppercase text-xs">
            <Link to="/admin/realtors">Cancel</Link>
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}
